import React, { useState, useEffect, useRef } from 'react';
import {
  doc,
  addDoc,
  collection,
  query,
  onSnapshot,
  orderBy,
  serverTimestamp,
  updateDoc,
  increment,
  setDoc
} from 'firebase/firestore';
import {
  Box,
  Typography,
  Paper,
  Alert,
  TextField,
  IconButton,
  CircularProgress,
  Button,
  Fade,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Send as SendIcon,
  Logout as LogOutIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  AccessTime as TimerIcon
} from '@mui/icons-material';
import GoogleIcon from '@mui/icons-material/Google';
import WindowIcon from '@mui/icons-material/Window';

export default function LiveChatView({ inquiryId, inquiryDetails, userId, onEndChat, db, appId }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);
  const [chatError, setChatError] = useState(null);

  // --- TIMEOUT STATE ---
  const [timeLeft, setTimeLeft] = useState(30);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const activityTimerRef = useRef(null);

  // ✅ Email menu state (NO tracking)
  const [emailAnchor, setEmailAnchor] = useState(null);
  const [emailCtx, setEmailCtx] = useState(null);

  // ✅ NEW: WhatsApp chooser dialog state
  const [waChooserOpen, setWaChooserOpen] = useState(false);

  const theme = useTheme();

  // ✅ NEW: WhatsApp Numbers (no +, no spaces)
  const WHATSAPP_NUMBERS = {
    zim: '263777223158', // +263 777 223 158
    sa: '27655326408',   // +27 65 532 6408
  };

  const closeEmailMenu = () => {
    setEmailAnchor(null);
    setEmailCtx(null);
  };

  const openEmailMenu = (e, ctx) => {
    e.preventDefault();
    e.stopPropagation();
    setEmailCtx(ctx);
    setEmailAnchor(e.currentTarget);
  };

  const openLink = (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const buildEmailLinks = ({ to, subject, body }) => {
    const enc = encodeURIComponent;

    const mailto =
      `mailto:${to}?subject=${enc(subject || '')}&body=${enc(body || '')}`;

    // Webmail compose links (reliable in browser)
    const gmail =
      `https://mail.google.com/mail/?view=cm&fs=1&to=${enc(to)}&su=${enc(subject || '')}&body=${enc(body || '')}`;

    const outlook =
      `https://outlook.office.com/mail/deeplink/compose?to=${enc(to)}&subject=${enc(subject || '')}&body=${enc(body || '')}`;

    const yahoo =
      `https://compose.mail.yahoo.com/?to=${enc(to)}&subject=${enc(subject || '')}&body=${enc(body || '')}`;

    return { mailto, gmail, outlook, yahoo };
  };

  const selectEmailProvider = (provider) => {
    if (!emailCtx) return;
    const links = buildEmailLinks(emailCtx);

    if (provider === 'gmail') openLink(links.gmail);
    else if (provider === 'outlook') openLink(links.outlook);
    else if (provider === 'yahoo') openLink(links.yahoo);
    else {
      // Default mail app (may still do nothing if no handler is configured)
      window.location.href = links.mailto;
    }

    closeEmailMenu();
  };

  // ✅ NEW: WhatsApp open helper
  const openWhatsApp = (countryKey, message) => {
    const number = WHATSAPP_NUMBERS[countryKey];
    if (!number) return;

    const encoded = encodeURIComponent(message || '');
    const url = `https://wa.me/${number}${encoded ? `?text=${encoded}` : ''}`;

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // --- 1. USER PRESENCE (UPDATED LOGIC) ---
  useEffect(() => {
    if (!userId || !db) return;

    const userStatusRef = doc(db, `userPresence/${userId}`);

    const setOnlineStatus = () => {
      setDoc(
        userStatusRef,
        {
          isOnline: true,
          lastSeen: serverTimestamp(),
          currentInquiryId: inquiryId
        },
        { merge: true }
      );
    };

    const setOfflineStatus = () => {
      setDoc(
        userStatusRef,
        {
          isOnline: false,
          lastSeen: serverTimestamp(),
          currentInquiryId: null
        },
        { merge: true }
      ).catch((err) => console.warn('Error setting offline:', err));
    };

    if (isTimedOut) {
      setOfflineStatus();
      return;
    }

    setOnlineStatus();
    const heartbeat = setInterval(setOnlineStatus, 5000);

    const handleTabClose = () => setOfflineStatus();
    window.addEventListener('beforeunload', handleTabClose);

    return () => {
      clearInterval(heartbeat);
      window.removeEventListener('beforeunload', handleTabClose);
      setOfflineStatus();
    };
  }, [userId, db, inquiryId, isTimedOut]);

  // --- 2. INACTIVITY TIMER LOGIC ---
  useEffect(() => {
    if (isTimedOut) return;

    activityTimerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(activityTimerRef.current);
          setIsTimedOut(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(activityTimerRef.current);
  }, [isTimedOut]);

  const handleTyping = (e) => {
    setNewMessage(e.target.value);
    if (!isTimedOut) setTimeLeft(30);
  };

  // --- 3. CHAT LOGIC ---
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (db && inquiryId && inquiryDetails?.userUnreadCount > 0) {
      const inquiryDocRef = doc(db, `allInquiries/${inquiryId}`);
      updateDoc(inquiryDocRef, {
        userUnreadCount: 0,
        lastReadByUserAt: serverTimestamp()
      }).catch((err) => console.warn('Could not mark messages as read:', err));
    }
  }, [db, inquiryId, inquiryDetails]);

  useEffect(() => {
    if (!db || !inquiryId || !userId || !appId) {
      setChatError('Chat dependencies are not available.');
      return () => {};
    }

    setChatError(null);
    const messagesPath = `artifacts/${appId}/users/${userId}/inquiries/${inquiryId}/messages`;
    const q = query(collection(db, messagesPath), orderBy('timestamp', 'asc'));

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const loadedMessages = querySnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        setMessages(loadedMessages);
        if (!isTimedOut) setTimeLeft(30);
      },
      (error) => {
        console.error(`Error fetching messages:`, error);
        setChatError(`Failed to load messages: ${error.message}.`);
      }
    );

    return () => unsubscribe();
  }, [db, inquiryId, userId, appId, isTimedOut]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    setTimeLeft(30);

    if (!newMessage.trim() || !db || !userId || !inquiryId || !appId) {
      setChatError('Cannot send message: Missing critical information.');
      return;
    }

    setIsSending(true);
    const messagesPath = `artifacts/${appId}/users/${userId}/inquiries/${inquiryId}/messages`;

    try {
      await addDoc(collection(db, messagesPath), {
        text: newMessage.trim(),
        sender: userId,
        senderType: 'user',
        timestamp: serverTimestamp(),
        read: false
      });

      await updateDoc(doc(db, `allInquiries/${inquiryId}`), {
        lastMessageSnippet: newMessage.trim().substring(0, 50),
        lastMessageBy: 'user',
        updatedAt: serverTimestamp(),
        status: 'active_chat',
        consultantUnreadCount: increment(1)
      });

      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      setChatError(`Failed to send message: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  // --- TIMEOUT VIEW ---
  if (isTimedOut) {
    const data = inquiryDetails?.formData || {};
    const type = inquiryDetails?.type;

    const typeLabels = {
      travelInquiry: 'Travel Inquiry',
      visaInquiry: 'Visa Inquiry',
      accommodationInquiry: 'Accommodation Inquiry',
      carHireInquiry: 'Car Hire Inquiry'
    };
    const displayType = typeLabels[type] || 'General Inquiry';

    let specificDetails = '';
    switch (type) {
      case 'travelInquiry':
        if (data.destination) specificDetails += `Destination: ${data.destination}\n`;
        if (data.travelDate) specificDetails += `Date: ${data.travelDate}\n`;
        if (data.travelPurpose) specificDetails += `Purpose: ${data.travelPurpose}\n`;
        break;
      case 'visaInquiry':
        if (data.nationality) specificDetails += `Nationality: ${data.nationality}\n`;
        if (data.destinationCountryVisa) specificDetails += `Visa For: ${data.destinationCountryVisa}\n`;
        if (data.visaType) specificDetails += `Visa Type: ${data.visaType}\n`;
        if (data.travelDateVisa) specificDetails += `Planned Date: ${data.travelDateVisa}\n`;
        break;
      case 'accommodationInquiry':
        if (data.location) specificDetails += `Location: ${data.location}\n`;
        if (data.checkInDate) specificDetails += `Check-in: ${data.checkInDate}\n`;
        if (data.checkOutDate) specificDetails += `Check-out: ${data.checkOutDate}\n`;
        if (data.roomType) specificDetails += `Room: ${data.roomType}\n`;
        if (data.guests) specificDetails += `Guests: ${data.guests}\n`;
        break;
      case 'carHireInquiry':
        if (data.carType) specificDetails += `Car Type: ${data.carType}\n`;
        if (data.pickupLocation) specificDetails += `Pick-up: ${data.pickupLocation}\n`;
        if (data.dropoffLocation) specificDetails += `Drop-off: ${data.dropoffLocation}\n`;
        if (data.startDate) specificDetails += `Start: ${data.startDate}\n`;
        if (data.endDate) specificDetails += `End: ${data.endDate}\n`;
        break;
      default:
        if (data.additionalNotes) specificDetails += `Note: ${data.additionalNotes}\n`;
    }

    if (data.additionalNotes && type !== 'general') {
      specificDetails += `Notes: ${data.additionalNotes}\n`;
    }

    // WhatsApp message (shared for both ZIM/SA)
    let waMsg = `Hello, I was chatting on the Abisiniya website regarding a *${displayType}* but the session timed out.\n\n`;
    waMsg += `*My Details:*\n`;
    if (data.fullName) waMsg += `👤 Name: ${data.fullName}\n`;
    if (data.email) waMsg += `📧 Email: ${data.email}\n`;
    if (data.phone) waMsg += `📱 Phone: ${data.phone}\n`;
    if (specificDetails) waMsg += `\n*Service Details:*\n${specificDetails}`;
    waMsg += `\nPlease assist me further.`;

    // Email context
    const emailSubject = `Inquiry: ${displayType} - ${data.fullName || 'Client'}`;
    let emailBody = `Hello Abisiniya Team,\n\nI was chatting on the website regarding a ${displayType} but the session timed out.\n\n`;
    emailBody += `--- MY DETAILS ---\n`;
    if (data.fullName) emailBody += `Name: ${data.fullName}\n`;
    if (data.email) emailBody += `Email: ${data.email}\n`;
    if (data.phone) emailBody += `Phone: ${data.phone}\n`;
    if (specificDetails) emailBody += `\n--- SERVICE DETAILS ---\n${specificDetails}`;
    emailBody += `\nPlease assist me further.`;

    const emailContext = {
      to: 'info@abisiniya.com',
      subject: emailSubject,
      body: emailBody
    };

    return (
      <Fade in={true}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            p: 4,
            textAlign: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.95)'
          }}
        >
          <TimerIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2, opacity: 0.5 }} />

          <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
            Are you still there?
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400 }}>
            The chat ended due to inactivity. You can continue this conversation via WhatsApp or Email with your details
            pre-filled:
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: 320 }}>
            {/* ✅ UPDATED: WhatsApp now opens chooser (ZIM / SA) */}
            <Button
              variant="contained"
              size="large"
              startIcon={<WhatsAppIcon />}
              onClick={() => setWaChooserOpen(true)}
              sx={{
                bgcolor: '#25D366',
                '&:hover': { bgcolor: '#128C7E' },
                color: '#fff',
                borderRadius: '25px',
                py: 1.5,
                fontWeight: 'bold'
              }}
            >
              Send WhatsApp
            </Button>

            {/* ✅ Email opens provider menu (no tracking) */}
            <Button
              variant="contained"
              size="large"
              startIcon={<EmailIcon />}
              onClick={(e) => openEmailMenu(e, emailContext)}
              sx={{
                bgcolor: theme.palette.primary.main,
                '&:hover': { bgcolor: theme.palette.primary.dark },
                color: '#fff',
                borderRadius: '25px',
                py: 1.5,
                fontWeight: 'bold'
              }}
            >
              Send Email
            </Button>

            <Button
              variant="text"
              onClick={onEndChat}
              sx={{ mt: 1, color: 'text.secondary', textDecoration: 'underline' }}
            >
              Start New Chat
            </Button>
          </Box>

          {/* ✅ NEW: WhatsApp chooser dialog */}
          <Dialog open={waChooserOpen} onClose={() => setWaChooserOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Choose WhatsApp line</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select which Abisiniya WhatsApp number you want to message:
              </Typography>

              <Stack spacing={1.2}>
                <Button
                  variant="contained"
                  startIcon={<WhatsAppIcon />}
                  onClick={() => {
                    setWaChooserOpen(false);
                    openWhatsApp('zim', waMsg);
                  }}
                  sx={{
                    bgcolor: '#25D366',
                    '&:hover': { bgcolor: '#128C7E' },
                    color: '#fff',
                    borderRadius: 2,
                    justifyContent: 'flex-start',
                    fontWeight: 700
                  }}
                >
                  Zimbabwe (+263 777 223 158)
                </Button>

                <Button
                  variant="contained"
                  startIcon={<WhatsAppIcon />}
                  onClick={() => {
                    setWaChooserOpen(false);
                    openWhatsApp('sa', waMsg);
                  }}
                  sx={{
                    bgcolor: '#25D366',
                    '&:hover': { bgcolor: '#128C7E' },
                    color: '#fff',
                    borderRadius: 2,
                    justifyContent: 'flex-start',
                    fontWeight: 700
                  }}
                >
                  South Africa (+27 65 532 6408)
                </Button>
              </Stack>
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setWaChooserOpen(false)}>Cancel</Button>
            </DialogActions>
          </Dialog>

          {/* Email Provider Menu */}
          <Menu
            anchorEl={emailAnchor}
            open={Boolean(emailAnchor)}
            onClose={closeEmailMenu}
            onClick={(e) => e.stopPropagation()}
            PaperProps={{ elevation: 3, sx: { borderRadius: 2, mt: 1, minWidth: 200 } }}
          >
            <MenuItem onClick={() => selectEmailProvider('gmail')}>
              <ListItemIcon>
                <GoogleIcon fontSize="small" sx={{ color: '#DB4437' }} />
              </ListItemIcon>
              <ListItemText>Gmail</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => selectEmailProvider('outlook')}>
              <ListItemIcon>
                <WindowIcon fontSize="small" sx={{ color: '#0078D4' }} />
              </ListItemIcon>
              <ListItemText>Outlook Web</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => selectEmailProvider('yahoo')}>
              <ListItemIcon>
                <EmailIcon fontSize="small" sx={{ color: '#6001D2' }} />
              </ListItemIcon>
              <ListItemText>Yahoo Mail</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => selectEmailProvider('email')}>
              <ListItemIcon>
                <EmailIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Default Mail App</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Fade>
    );
  }

  // --- ACTIVE CHAT VIEW (unchanged) ---
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', flexGrow: 1, minHeight: 0, position: 'relative' }}>
      <Box
        sx={{
          position: 'absolute',
          top: 10,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
          background: 'rgba(219, 50, 50, 0.15)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          borderRadius: '20px',
          padding: '4px 16px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <TimerIcon sx={{ fontSize: 16, color: timeLeft < 10 ? 'error.main' : 'text.primary' }} />
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: timeLeft < 10 ? 'error.main' : 'text.primary' }}>
          Session closing in {timeLeft}s
        </Typography>
      </Box>

      <Typography variant="h6" sx={{ textAlign: 'center', mb: 1.5, flexShrink: 0, mt: 4 }}>
        Chat with Consultant for: {inquiryDetails?.type.replace('Inquiry', ' Inquiry') || 'Inquiry'}
      </Typography>

      {chatError && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setChatError(null)}>
          {chatError}
        </Alert>
      )}

      <Paper
        variant="outlined"
        sx={{ flexGrow: 1, overflowY: 'auto', p: 1.5, mb: 1.5, bgcolor: 'background.default', borderRadius: 2, minHeight: 0 }}
      >
        {messages.map((msg) => (
          <Box
            key={msg.id}
            sx={{
              mb: 1,
              display: 'flex',
              justifyContent: msg.sender === userId || msg.senderType === 'user' ? 'flex-end' : 'flex-start'
            }}
          >
            <Paper
              elevation={1}
              sx={{
                p: '6px 12px',
                borderRadius: msg.sender === userId || msg.senderType === 'user' ? '10px 10px 0 10px' : '10px 10px 10px 0',
                bgcolor:
                  msg.sender === userId || msg.senderType === 'user'
                    ? 'primary.main'
                    : msg.senderType === 'system'
                    ? theme.palette.info.light
                    : 'grey.200',
                color:
                  msg.sender === userId || msg.senderType === 'user'
                    ? 'primary.contrastText'
                    : msg.senderType === 'system'
                    ? theme.palette.info.dark
                    : 'text.primary',
                maxWidth: '80%',
                wordBreak: 'break-word'
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {msg.text}
              </Typography>
              <Typography variant="caption" display="block" sx={{ textAlign: 'right', mt: 0.25, fontSize: '0.65rem', opacity: 0.8 }}>
                {msg.timestamp?.toDate
                  ? msg.timestamp.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                  : msg.senderType !== 'system' && 'Sending...'}
              </Typography>
            </Paper>
          </Box>
        ))}
        <div ref={messagesEndRef} />
      </Paper>

      <Box component="form" onSubmit={handleSendMessage} sx={{ display: 'flex', alignItems: 'center', p: 0.5, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Type your message..."
          value={newMessage}
          onChange={handleTyping}
          sx={{ mr: 1 }}
        />
        <IconButton type="submit" color="primary" disabled={isSending || !newMessage.trim()} sx={{ p: '8px' }}>
          {isSending ? <CircularProgress size={20} /> : <SendIcon sx={{ fontSize: '1.4rem' }} />}
        </IconButton>
      </Box>

      <Button variant="outlined" color="error" size="small" onClick={onEndChat} sx={{ mt: 1.5, alignSelf: 'center' }} startIcon={<LogOutIcon />}>
        End Chat & Start New Inquiry
      </Button>
    </Box>
  );
}
